<script setup lang="ts">
import type { INavLinkItem } from '~/components/navbar/NavigationLinks.vue'
import type { MegamenuNavItem } from '~/data/megamenu'
import { sendMenuClick } from '~/composables/useGtmEvents'

const props = defineProps<{
  navLinks: INavLinkItem[]
  megamenuData: MegamenuNavItem[]
}>()

const emit = defineEmits<{
  (e: 'navbarClose'): void
}>()

const resolveHref = useFullLinkUrl()
const hoveredIndex = ref<number | null>(null)
const closeTimeout = ref<ReturnType<typeof setTimeout> | null>(null)

/** Plain link: close submenu quickly; still cancellable if pointer reaches another dropdown. */
const PLAIN_LINK_CLOSE_MS = 80
/** Leaving the whole megamenu: slightly longer so pointer can move into the open panel. */
const NAV_LEAVE_CLOSE_MS = 150

const itemByPath = computed(() => {
  const map = new Map<string, MegamenuNavItem>()
  for (const item of props.megamenuData) {
    map.set(item.path, item)
  }
  return map
})

function getMegamenuItem(path: string): MegamenuNavItem | undefined {
  return itemByPath.value.get(path)
}

function hasDropdown(item: INavLinkItem) {
  const full = getMegamenuItem(item.path)
  return (full?.sections?.length ?? 0) > 0 || (full?.items?.length ?? 0) > 0
}

function onNavEnter(index: number) {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
  // Switch immediately so moving between items (or past a plain link) does not show an empty gap
  hoveredIndex.value = index
}

function scheduleMenuClose(delayMs: number = NAV_LEAVE_CLOSE_MS) {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
  closeTimeout.value = setTimeout(() => {
    hoveredIndex.value = null
    closeTimeout.value = null
  }, delayMs)
}

function onPlainLinkEnter() {
  scheduleMenuClose(PLAIN_LINK_CLOSE_MS)
}

function onNavLeave() {
  scheduleMenuClose(NAV_LEAVE_CLOSE_MS)
}

function onDropdownEnter() {
  if (closeTimeout.value) {
    clearTimeout(closeTimeout.value)
    closeTimeout.value = null
  }
}

function onDropdownLeave() {
  onNavLeave()
}

function setHoverOrigin(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  if (el.dataset.originLocked === 'true') return
  const rect = el.getBoundingClientRect()
  const x = e.clientX - rect.left
  const third = rect.width / 3
  if (x < third) el.style.setProperty('--hover-origin', 'left')
  else if (x > rect.width - third)
    el.style.setProperty('--hover-origin', 'right')
  else el.style.setProperty('--hover-origin', 'center')
  el.dataset.originLocked = 'true'
}

function resetHoverOrigin(e: MouseEvent) {
  const el = e.currentTarget as HTMLElement
  setTimeout(() => {
    el.dataset.originLocked = 'false'
    el.style.removeProperty('--hover-origin')
  }, 300)
}

function handleNavClick(item: INavLinkItem) {
  sendMenuClick({
    menuName: item.text,
    itemLabel: item.text,
    itemPath: item.path,
  })
  emit('navbarClose')
}
</script>

<template>
  <div
    data-test-id="megamenu-desktop"
    class="w-full"
    @mouseenter="onDropdownEnter"
    @mouseleave="onDropdownLeave"
  >
    <ul
      data-test-id="megamenu-desktop-nav-list"
      class="flex flex-col md:flex-row md:w-full md:max-w-[630px] md:justify-between font-medium mt-0 space-y-4 md:space-y-0 rtl:space-x-reverse text-sm"
    >
      <li
        v-for="(item, index) in navLinks"
        :key="item.path"
        :data-test-id="`megamenu-desktop-nav-item-${index}`"
        class="border-b pb-4 md:border-none md:pb-0 relative"
      >
        <a
          v-if="!hasDropdown(item)"
          :data-test-id="`megamenu-desktop-nav-link-${index}`"
          class="text-stone-800 text-sm font-normal navigation-link block no-underline"
          :href="resolveHref(item.path)"
          @mouseenter="onPlainLinkEnter"
          @click="handleNavClick(item)"
        >
          {{ item.text }}
        </a>
        <a
          v-else
          :data-test-id="`megamenu-desktop-nav-link-${index}`"
          class="text-stone-800 text-sm font-normal navigation-link block no-underline"
          :class="{ 'navigation-link--active': hoveredIndex === index }"
          :href="resolveHref(item.path)"
          @click="handleNavClick(item)"
          @mouseenter="
            (e: MouseEvent) => {
              setHoverOrigin(e)
              onNavEnter(index)
            }
          "
          @mouseleave="resetHoverOrigin"
        >
          {{ item.text }}
        </a>
      </li>
    </ul>
    <template v-for="(item, index) in navLinks" :key="item.path">
      <MegamenuDropdown
        v-if="hasDropdown(item)"
        :item="getMegamenuItem(item.path)!"
        :active="hoveredIndex === index"
        @mouseenter="onDropdownEnter"
      />
    </template>
  </div>
</template>

<style lang="postcss" scoped>
.navigation-link {
  @apply relative inline-block no-underline;
}
.navigation-link.router-link-active,
.navigation-link.router-link-exact-active,
.navigation-link--active,
.navigation-link:hover {
  color: #032e32;
}
.navigation-link::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #4ab5bd;
  transform: scaleX(0);
  transform-origin: var(--hover-origin, center);
  transition:
    transform 0.3s ease,
    transform-origin 0.1s ease;
}
.navigation-link.router-link-active::after,
.navigation-link.router-link-exact-active::after,
.navigation-link--active::after,
.navigation-link:hover::after {
  transform: scaleX(1);
}
</style>
